import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, Chip, Switch, Tooltip, Typography} from "@mui/material";

import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import {approvalMessage} from "../../util/approvalMessages";
import {classificationByCode} from "../../util/objectClassifications";
import ApprovalCellEditor from "./ApprovalCellEditor";
import ApprovalMatrixHistory from "./ApprovalMatrixHistory";
import ApprovalTab from "./ApprovalTab";

type Row = Record<string, any>;

// Who must sign an object revision before it can be released, per OBJECT TYPE.
//
// A LIST, NOT A GRID, and that is the tier difference made visible. t5's matrix is genuinely
// two-dimensional -- governance classes down, six record types across -- because its record types
// are fixed and structural and it needed a second axis bolted on to be worth selling. Foundation
// governs ONE kind of record and its second axis is the object type, which is user-defined and
// already means what a governance category means. So the honest shape is one row per object type,
// and drawing a grid with a single meaningful column would be borrowing t5's furniture to make an
// empty room look furnished.
//
// The matrix's own row sits apart, under its own heading: it is not an object type, and listing it
// among them would invite somebody to read "Approval Matrix" as a kind of document.

const MATRIX_TARGET = "ObjectApprovalMatrix";
const OBJECT_TARGET = "Object";
const CHANGE_ORDER_CLASSIFICATION = "ChangeOrder";

/**
 * Which TARGET a type's cell belongs to.
 *
 * Every row on this screen is an OBJECT_TYPE, but they are not all approved as objects. A change
 * order is typed exactly as a record is -- its kinds are OBJECT_TYPE rows with classification
 * "ChangeOrder" -- and the approval engine resolves its cell under TARGET_TYPE "ChangeOrder".
 * Writing an "Object" cell for one would store a requirement that _submission_cell never looks for:
 * the screen would show approvers configured and the change order would still submit with none.
 */
const PURCHASE_ORDER_CLASSIFICATION = "PurchaseOrder";
const formatAmount = (value: any) => {
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString(undefined, {maximumFractionDigits: 2}) : String(value ?? "");
};
const targetFor = (classification?: string | null) =>
    classification === CHANGE_ORDER_CLASSIFICATION ? CHANGE_ORDER_CLASSIFICATION : OBJECT_TARGET;

/**
 * What one row's approvers amount to, in the three words a chip has room for.
 *
 * THE DISTINCTION THIS DRAWS IS THE WHOLE POINT, and it is the one an earlier cut of this screen got
 * wrong. An empty row means two opposite things depending on where it sits. On an object type it
 * means "whatever the default says" -- ordinary, and usually governed. On the DEFAULT row it means
 * nothing is required of anything that falls through to it, so those records release unwitnessed.
 * Rendering both as a blank cell, or both as an identical amber "none", tells the person configuring
 * the matrix that the safe case and the hole look the same. They do not, and this says so.
 *
 * Pure, exported, and tested directly: the rule it encodes decides whether an ungoverned type is
 * noticed, which is too much to leave to whether the chip happens to look right.
 */
export function cellSummary(cell: Row | null | undefined, isDefault: boolean) {
    const slots = cell?.Slots ?? [];
    if (!slots.length) {
        return isDefault
            ? {kind: "none", text: "none", stages: 0}
            : {kind: "inherits", text: "default", stages: 0};
    }
    const stages = new Set(slots.map((one: Row) => one.Stage ?? 1));
    return {
        kind: "required",
        text: stages.size > 1 ? `⇥ ${slots.length}` : `${slots.length}`,
        stages: stages.size,
    };
}

export default function ObjectApprovalMatrixSection() {
    const [matrix, setMatrix] = useState<Row | null>(null);
    const [objectTypes, setObjectTypes] = useState<Row[]>([]);
    const [personas, setPersonas] = useState<Row[]>([]);
    const [message, setMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<Row | null>(null);
    const [saving, setSaving] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [refused, setRefused] = useState(false);
    const [showingHistory, setShowingHistory] = useState(false);
    const [viewing, setViewing] = useState<string | null>(null);
    const [approvalState, setApprovalState] = useState<Row | null>(null);
    // THE DRAFT'S APPROVAL STATE, EVEN WHEN THE DRAFT IS NOT ON SCREEN. The page opens on the
    // RELEASED revision and the approval panel renders only for a draft -- so an approver following
    // the "needs your signature" notification landed here, saw the released matrix, and had no way
    // to know the thing awaiting them was one unlabelled button away. Fixed in the Catalog after
    // Nina Patel's first login ("clicking the notification did nothing"); shipped correct here.
    const [draftApproval, setDraftApproval] = useState<Row | null>(null);

    const sessionContext = useSessionContext();
    const api = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api({method: "GET", url: "t1/v1/object_approval_matrix",
                 params: viewing ? {RevisionUSID: viewing} : {}}),
            api({method: "GET", url: "t1/v1/object_types", params: {Active: "true", Limit: 300}}),
            api({method: "GET", url: "t1/v1/object_approval_matrix", params: {Personas: true}}),
        ])
            .then(([matrixResponse, typeResponse, personaResponse]) => {
                setMatrix(matrixResponse.data ?? null);
                const list = typeResponse?.data?.PlatformMessage?.ListOfObjectType?.ObjectType ?? [];
                setObjectTypes(Array.isArray(list) ? list : [list]);
                setPersonas(personaResponse.data?.Personas ?? []);

                const draft = (matrixResponse.data?.Revisions ?? [])
                    .find((one: Row) => one.Status === "DRAFT");
                if (!draft || draft.USID === matrixResponse.data?.Revision?.USID) {
                    setDraftApproval(null);
                    return;
                }
                api({method: "GET", url: "t1/v1/object_approval_requests",
                     params: {TargetType: MATRIX_TARGET, RecordUSID: draft.USID}})
                    .then(response => setDraftApproval(response.data ?? null))
                    .catch(() => setDraftApproval(null));
            })
            .catch((error: any) => {
                // TYPED, because these two say opposite things to the person reading them. A 403
                // is the product working: this screen is administration and they are not an
                // administrator. Anything else is a fault, and calling it a permissions problem
                // would send somebody to ask for a role they already have.
                setRefused(error?.response?.status === 403);
                setMessage(error?.response?.status === 403
                    ? null : "Could not load the approval matrix.");
            })
            .finally(() => setLoading(false));
    }, [api, viewing]);

    // RELOAD THE MATRIX WHEN THE APPROVAL SAYS IT MUST HAVE CHANGED. The header and the approval
    // panel fetch separately, so a signature made in SOMEBODY ELSE'S browser refreshes the panel and
    // leaves the header describing a revision that no longer exists.
    //
    // Guarded by a ref rather than by the dependency array: reloading sets state that re-renders the
    // tab, which reports again, which would reload for ever.
    const reloadedFor = React.useRef<string | null>(null);
    const handleApprovalState = useCallback((next: Row | null) => {
        setApprovalState(next);
        const approved = (next?.Requests ?? []).find((one: Row) => one.Status === "APPROVED");
        if (approved && reloadedFor.current !== approved.USID) {
            reloadedFor.current = approved.USID;
            load();
        }
    }, [load]);

    useEffect(() => { load(); }, [load]);

    const revision = matrix?.Revision ?? null;
    const isDraft = revision?.Status === "DRAFT";
    const cells: Row[] = matrix?.Cells ?? [];
    const draftRevision = (matrix?.Revisions ?? []).find((one: Row) => one.Status === "DRAFT");

    const draftPending = (draftApproval?.Requests ?? [])
        .find((one: Row) => one.Status === "PENDING") ?? null;
    // CanSign COMES FROM THE SERVER, the only thing that knows this caller's personas and the rules
    // that go with them -- author-exclusion and distinctness included. Asking it is what makes
    // "needs your signature" true rather than merely likely.
    const iCanSignDraft = (draftPending?.Slots ?? []).some((one: Row) => one.CanSign);

    const pendingRequest = (approvalState?.Requests ?? [])
        .find((one: Row) => one.Status === "PENDING") ?? null;
    const latestRequest = (approvalState?.Requests ?? [])[0] ?? null;
    const resubmittable = !latestRequest
        || ["WITHDRAWN", "REJECTED"].includes(latestRequest.Status);

    // NOTHING TO CONFIGURE UNTIL SOMETHING EXISTS TO CONFIGURE. A realm that has never set up a
    // matrix has no revision at all, so there is nothing to edit and nothing for Revise to revise
    // from -- the screen without this was a complete, correct, permanently read-only list.
    //
    // SEEDED RATHER THAN SAVED, and the difference is the whole point. A save would create a DRAFT,
    // and the only door out of DRAFT is an approval request -- deliberately, so that changing the
    // rules obeys the rules. But the request resolves against the RELEASED matrix, and there isn't
    // one, so the first draft would be unreleasable for ever. Seeding is the platform's answer to
    // its own chicken-and-egg: revision 1 arrives RELEASED and empty, governing nothing, and every
    // revision after it goes through approval as designed.
    const create = (slots: Row[]) => {
        setSaving(true);
        api({method: "POST", url: "t1/v1/object_approval_matrix", data: {Seed: true, Cells: [{
            TargetType: MATRIX_TARGET, ObjectTypeUSID: null, Slots: slots,
        }]}})
            .then(() => { setSeeding(false); load(); })
            .catch((error: any) => setMessage(approvalMessage(error, "Could not set up the matrix.")))
            .finally(() => setSaving(false));
    };

    const revise = () => {
        if (!revision) return;
        api({method: "POST", url: "t1/v1/object_approval_matrix",
             data: {USID: revision.USID, LifecycleAction: "Revise"}})
            .then(() => load())
            .catch((error: any) => setMessage(approvalMessage(error, "Could not revise the matrix.")));
    };

    const grouped = useMemo(() => {
        const byClassification = new Map<string, Row[]>();
        objectTypes.forEach((type: Row) => {
            const key = type.ObjectClassification ?? "";
            if (!byClassification.has(key)) byClassification.set(key, []);
            byClassification.get(key)!.push(type);
        });
        // Sorted, because the API returns creation order and a reader looking for one type among
        // forty has no way to know that.
        byClassification.forEach(types => types.sort((a: Row, b: Row) =>
            String(a.Name ?? "").localeCompare(String(b.Name ?? ""))));
        return Array.from(byClassification.entries())
            .sort(([a], [b]) => (classificationByCode(a)?.plural ?? a)
                .localeCompare(classificationByCode(b)?.plural ?? b));
    }, [objectTypes]);

    // A CELL IS (target, type, amount band). The band is null for every row but a purchase order
    // type's amount bands (2026-10-30, approval by amount): "orders of this type up to this total
    // need these signatures". The same type can have several bands and an unbanded row behind them.
    const sameCell = (one: Row, targetType: string, objectTypeUSID: string | null, amountUpTo: string | null) =>
        one.TargetType === targetType && (one.ObjectTypeUSID ?? null) === objectTypeUSID
            && (one.AmountUpTo ?? null) === amountUpTo;
    const cellFor = (targetType: string, objectTypeUSID: string | null, amountUpTo: string | null = null) =>
        cells.find((one: Row) => sameCell(one, targetType, objectTypeUSID, amountUpTo)) ?? null;
    const bandsFor = (targetType: string, objectTypeUSID: string | null) =>
        cells.filter((one: Row) => one.TargetType === targetType
            && (one.ObjectTypeUSID ?? null) === objectTypeUSID && one.AmountUpTo != null)
            .sort((a: Row, b: Row) => Number(a.AmountUpTo) - Number(b.AmountUpTo));

    const openCell = (targetType: string, objectTypeUSID: string | null,
                      heading: string, description: string, amountUpTo: string | null = null,
                      banded = false) =>
        setEditing({
            TargetType: targetType,
            ObjectTypeUSID: objectTypeUSID,
            AmountUpTo: amountUpTo,
            Banded: banded,
            Heading: heading,
            Description: description,
            Slots: cellFor(targetType, objectTypeUSID, amountUpTo)?.Slots ?? [],
        });

    const applyCell = (slots: Row[], amountUpTo: string | null = null) => {
        if (!editing) return;
        // The row being edited is replaced by its (type, band) key as it WAS opened; a band whose
        // amount was changed in the editor is therefore moved, not duplicated.
        const others = cells.filter((one: Row) => !sameCell(one, editing.TargetType,
            editing.ObjectTypeUSID ?? null, editing.AmountUpTo ?? null));
        const payload = [...others.map((one: Row) => ({
            TargetType: one.TargetType,
            ObjectTypeUSID: one.ObjectTypeUSID ?? null,
            AmountUpTo: one.AmountUpTo ?? null,
            Slots: one.Slots ?? [],
        })), {
            TargetType: editing.TargetType,
            ObjectTypeUSID: editing.ObjectTypeUSID ?? null,
            AmountUpTo: editing.Banded ? amountUpTo : null,
            Slots: slots,
        }];

        setSaving(true);
        api({method: "PUT", url: "t1/v1/object_approval_matrix", data: {Cells: payload}})
            .then(() => { setEditing(null); load(); })
            .catch((error: any) => setMessage(approvalMessage(error, "Could not save that.")))
            .finally(() => setSaving(false));
    };

    const rowStyle: React.CSSProperties = {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", borderBottom: "1px solid #E4E7EC", cursor: isDraft ? "pointer" : "default",
    };

    const countChip = (cell: Row | null, isDefault: boolean) => {
        const {kind, text} = cellSummary(cell, isDefault);
        const tooltip = kind === "none"
            ? "Nothing is required, so records falling through to this row release unwitnessed."
            : kind === "inherits"
                ? "No approvers of its own — this type uses the default row."
                : "Approvers required before a revision of this type can be released.";
        return (
            <Tooltip title={tooltip}>
                <Chip size="small" label={text}
                      color={kind === "none" ? "warning" : "default"}
                      variant={kind === "required" ? "filled" : "outlined"}
                      sx={{fontSize: 11, height: 20, minWidth: 56,
                           color: kind === "inherits" ? "#8A94A6" : undefined}}/>
            </Tooltip>
        );
    };

    if (loading) return <Typography style={{fontSize: 13, margin: 24}}>Loading…</Typography>;

    if (refused) {
        return (
            <div style={{margin: "16px 30px 40px 30px", maxWidth: 620}}>
                <Typography style={{fontSize: 15, fontWeight: 600, marginBottom: 6}}>
                    Object Approval Matrix
                </Typography>
                <Typography style={{fontSize: 13, color: "#6B7280"}}>
                    Who must sign what is configured by an administrator. You can still see what is
                    waiting on you under <b>My Approvals</b>, and any record's own Approvals tab
                    shows what it requires.
                </Typography>
            </div>
        );
    }

    return (
        <div style={{margin: "16px 30px 40px 30px"}}>
            <div style={{display: "flex", alignItems: "center", gap: 12, marginBottom: 6}}>
                <Typography style={{fontSize: 15, fontWeight: 600}}>Object Approval Matrix</Typography>
                {revision ? (
                    <>
                        <Chip size="small" label={`Revision ${revision.Revision}`} sx={{fontSize: 11, height: 20}}/>
                        <Chip size="small" label={revision.Status} color={isDraft ? "default" : "success"}
                              sx={{fontSize: 11, height: 20}}/>
                        {isDraft && pendingRequest ? (
                            <Chip size="small" color="warning"
                                  label={`Submitted for approval — ${pendingRequest.SlotsSigned ?? 0}`
                                      + `/${pendingRequest.SlotsTotal ?? 0} signed`}
                                  sx={{fontSize: 11, height: 20}}/>
                        ) : null}
                        {isDraft && !pendingRequest && resubmittable ? (
                            <Chip size="small" variant="outlined" label="Not submitted"
                                  sx={{fontSize: 11, height: 20}}/>
                        ) : null}
                        <Tooltip title={isDraft
                            ? "Whether the person who created a record may also sign for it. Off means a record is always witnessed by somebody else. Takes effect when this revision is approved."
                            : "Whether the person who created a record may also sign for it. Revise to change it."}>
                            <span style={{display: "inline-flex", alignItems: "center", gap: 4}}>
                                <Switch size="small" checked={revision.AuthorMaySign === true}
                                        disabled={!isDraft || saving}
                                        onChange={event => {
                                            setSaving(true);
                                            api({method: "PUT", url: "t1/v1/object_approval_matrix",
                                                 data: {AuthorMaySign: event.target.checked}})
                                                .then(() => load())
                                                .catch((error: any) => setMessage(approvalMessage(error, "Could not change that.")))
                                                .finally(() => setSaving(false));
                                        }}/>
                                <Typography style={{fontSize: 12, color: "#6B7280"}}>
                                    {revision.AuthorMaySign === true ? "Creator may approve" : "Creator may not approve"}
                                </Typography>
                            </span>
                        </Tooltip>
                        <Tooltip title={isDraft
                            ? "Whether approvals gate releases in this realm. Takes effect when this revision is approved."
                            : "Whether approvals gate releases in this realm. Revise to change it."}>
                            <span style={{display: "inline-flex", alignItems: "center", gap: 4}}>
                                <Switch size="small" checked={revision.IsActive !== false}
                                        disabled={!isDraft || saving}
                                        onChange={event => {
                                            setSaving(true);
                                            api({method: "PUT", url: "t1/v1/object_approval_matrix",
                                                 data: {IsActive: event.target.checked}})
                                                .then(() => load())
                                                .catch((error: any) => setMessage(approvalMessage(error, "Could not change that.")))
                                                .finally(() => setSaving(false));
                                        }}/>
                                <Typography style={{fontSize: 12, color: "#6B7280"}}>
                                    {revision.IsActive === false ? "Approvals off" : "Approvals on"}
                                </Typography>
                            </span>
                        </Tooltip>
                    </>
                ) : null}
                {draftRevision && revision && draftRevision.USID !== revision.USID ? (
                    <Button size="small" variant="outlined" onClick={() => setViewing(draftRevision.USID)}
                            sx={{textTransform: "none", fontSize: 13}}>
                        Open draft revision {draftRevision.Revision}
                    </Button>
                ) : null}
                {viewing ? (
                    <Button size="small" onClick={() => setViewing(null)} sx={{textTransform: "none", fontSize: 13}}>
                        Show the released revision
                    </Button>
                ) : null}
                {!revision ? (
                    <Chip size="small" variant="outlined" label="Not configured"
                          sx={{fontSize: 11, height: 20}}/>
                ) : null}
                <div style={{flexGrow: 1}}/>
                {!revision ? (
                    <Button size="small" variant="contained" disabled={saving}
                            onClick={() => setSeeding(true)}
                            sx={{textTransform: "none", fontSize: 13}}>
                        Set up the matrix
                    </Button>
                ) : null}
                {/* A DRAFT THE SCREEN DOES NOT MENTION IS WORSE THAN NO DRAFT: offering Revise while
                    one already exists means the refusal cites a reason nobody could have known. */}
                {revision && !isDraft && !draftRevision ? (
                    <Button size="small" onClick={revise} sx={{textTransform: "none", fontSize: 13}}>
                        Revise
                    </Button>
                ) : null}
                <Button size="small" onClick={() => setShowingHistory(true)}
                        sx={{textTransform: "none", fontSize: 13}}>History</Button>
            </div>

            <Typography style={{fontSize: 13, color: "#6B7280", maxWidth: 760, marginBottom: 14}}>
                {!revision ? (
                    <><b>This realm has no approval matrix.</b> Every object releases directly, with
                    nobody's signature on it. Setting one up creates a draft you can configure; it
                    governs nothing until that draft is itself approved.</>
                ) : (
                    <>Who must sign a revision before it can be released, per object type. A type
                    with no approvers of its own uses the default row; a type with none and no
                    default releases directly, unwitnessed.</>
                )}
            </Typography>

            {/* THE PAGE OPENS ON THE RELEASED REVISION and the approval panel exists only on the
                draft, so without this an approver arriving from a notification sees the matrix as it
                stands and nothing to act on -- the notification right, the page simply silent about
                the thing it was about. */}
            {draftRevision && revision && draftRevision.USID !== revision.USID && draftPending ? (
                <div style={{display: "flex", alignItems: "center", gap: 12, margin: "0 0 14px 0",
                             padding: "10px 14px", borderRadius: 8,
                             border: `1px solid ${iCanSignDraft ? "#F5C26B" : "#D8DDE4"}`,
                             background: iCanSignDraft ? "#FFF8EC" : "#F7F9FB"}}>
                    <Typography style={{fontSize: 13}}>
                        {iCanSignDraft ? (
                            <><b>Revision {draftRevision.Revision} needs your signature.</b>{" "}
                            You are looking at the released matrix; the change awaiting you is in
                            the draft.</>
                        ) : (
                            <><b>Revision {draftRevision.Revision} is submitted for approval</b>
                            {" "}&mdash; {draftPending.SlotsSigned ?? 0}/{draftPending.SlotsTotal ?? 0}
                            {" "}signed. You are looking at the released matrix.</>
                        )}
                    </Typography>
                    <Button size="small" variant={iCanSignDraft ? "contained" : "outlined"}
                            color={iCanSignDraft ? "warning" : "primary"}
                            onClick={() => setViewing(draftRevision.USID)}
                            sx={{textTransform: "none", fontSize: 13, whiteSpace: "nowrap"}}>
                        {iCanSignDraft ? "Open and sign" : `Open revision ${draftRevision.Revision}`}
                    </Button>
                </div>
            ) : null}

            {message ? (
                <Typography style={{fontSize: 13, color: "#B42318", marginBottom: 10}}>{message}</Typography>
            ) : null}

            <div style={{border: "1px solid #E4E7EC", borderRadius: 8, overflow: "hidden"}}>
                <div style={{...rowStyle, background: "#F7F9FB", cursor: "default"}}>
                    <Typography style={{fontSize: 12, fontWeight: 600, color: "#6B7280"}}>OBJECT TYPE</Typography>
                    <Typography style={{fontSize: 12, fontWeight: 600, color: "#6B7280"}}>APPROVERS</Typography>
                </div>

                <div style={rowStyle} onClick={() => isDraft && openCell(OBJECT_TARGET, null, "Every object type",
                                          "Objects of a type with no approvers of its own need these signatures.")}>
                    <Typography style={{fontSize: 14, fontWeight: 600}}>(default)</Typography>
                    {countChip(cellFor(OBJECT_TARGET, null), true)}
                </div>

                {/* GROUPED BY CLASSIFICATION because the type list is as long as the tenant made it,
                    and a flat run of forty names sorted by nothing in particular is a list you scan
                    rather than read. The grouping is the one the rest of Foundation already uses --
                    the same axis the rail is built on -- so a Document type is found where documents
                    live. */}
                {grouped.map(([classification, types]) => (
                    <React.Fragment key={classification}>
                        <div style={{padding: "6px 14px", background: "#FBFCFD",
                                     borderBottom: "1px solid #EEF1F4"}}>
                            <Typography style={{fontSize: 11, fontWeight: 600, color: "#8A94A6",
                                                textTransform: "uppercase", letterSpacing: "0.4px"}}>
                                {classificationByCode(classification)?.plural
                                    ?? (classification === CHANGE_ORDER_CLASSIFICATION
                                        ? "Change Orders" : classification)}
                            </Typography>
                        </div>
                        {types.map((type: Row) => (
                            <React.Fragment key={type.USID}>
                                <div style={rowStyle}
                                     onClick={() => isDraft && openCell(targetFor(classification), type.USID, type.Name,
                                                      classification === CHANGE_ORDER_CLASSIFICATION
                                                          ? `A ${type.Name} needs these signatures, on top of whatever the records inside it require.`
                                                          : classification === PURCHASE_ORDER_CLASSIFICATION
                                                              ? `Orders of type ${type.Name} above every amount band, or with no bands, need these signatures.`
                                                              : `Objects of type ${type.Name} need these signatures.`)}>
                                    <Typography style={{fontSize: 14}}>{type.Name}</Typography>
                                    {countChip(cellFor(targetFor(classification), type.USID), false)}
                                </div>
                                {/* APPROVAL BY AMOUNT, for a purchase order type only: one row per band,
                                    smallest first. An order is governed by the smallest band that covers
                                    its total, else by the type's own row above; with bands and no row
                                    above, an order above every band cannot be submitted. */}
                                {classification === PURCHASE_ORDER_CLASSIFICATION ? (
                                    <>
                                        {bandsFor(OBJECT_TARGET, type.USID).map((band: Row) => (
                                            <div key={`${type.USID}:${band.AmountUpTo}`}
                                                 style={{...rowStyle, paddingLeft: 34, background: "#FBFCFD"}}
                                                 onClick={() => isDraft && openCell(OBJECT_TARGET, type.USID,
                                                     `${type.Name} — up to ${formatAmount(band.AmountUpTo)}`,
                                                     `Orders of type ${type.Name} whose total is at most this amount need these signatures.`,
                                                     band.AmountUpTo, true)}>
                                                <Typography style={{fontSize: 13, color: "#475467"}}>
                                                    up to {formatAmount(band.AmountUpTo)}
                                                </Typography>
                                                {countChip(band, false)}
                                            </div>
                                        ))}
                                        {isDraft ? (
                                            <div style={{...rowStyle, paddingLeft: 34, background: "#FBFCFD"}}
                                                 onClick={() => openCell(OBJECT_TARGET, type.USID,
                                                     `${type.Name} — a new amount band`,
                                                     `Orders of type ${type.Name} whose total is at most the amount you give need these signatures.`,
                                                     null, true)}>
                                                <Typography style={{fontSize: 13, color: "#1F6FEB"}}>
                                                    + Add an amount band
                                                </Typography>
                                            </div>
                                        ) : null}
                                    </>
                                ) : null}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </div>

            {/* THE MATRIX GOVERNS ITSELF, and says so apart from the types it governs. Changing what
                the realm requires is a governed change like any other -- which is what makes "who
                approved the change to the approval rules" answerable. */}
            <Typography style={{fontSize: 14, fontWeight: 600, margin: "22px 0 6px 0"}}>
                The matrix itself
            </Typography>
            <div style={{border: "1px solid #E4E7EC", borderRadius: 8, overflow: "hidden", maxWidth: 520}}>
                <div style={rowStyle} onClick={() => isDraft && openCell(MATRIX_TARGET, null, "The approval matrix",
                                          "Changes to this matrix need these signatures before they take effect.")}>
                    <Typography style={{fontSize: 14}}>Changes to this matrix</Typography>
                    {countChip(cellFor(MATRIX_TARGET, null), true)}
                </div>
            </div>

            {isDraft && revision ? (
                <div style={{margin: "24px 0 0 0", borderTop: "1px solid #E4E7EC"}}>
                    <Typography style={{fontSize: 14, fontWeight: 600, margin: "18px 0 2px 0"}}>
                        Approval of this matrix revision
                    </Typography>
                    <Typography style={{fontSize: 13, color: "#6B7280", marginBottom: 6, maxWidth: 620}}>
                        Who must sign before revision {revision.Revision} replaces the released one.
                        These are the approvers the matrix names <b>for itself</b>, in the row above
                        &mdash; not the approvers you are configuring for object types.
                    </Typography>
                    <ApprovalTab targetType={MATRIX_TARGET} recordUSID={revision.USID}
                                 recordStatus={revision.Status} showClass={false}
                                 onState={handleApprovalState}/>
                </div>
            ) : null}

            <ApprovalMatrixHistory open={showingHistory} onClose={() => setShowingHistory(false)}/>

            {/* WHO MAY CHANGE THE RULES IS THE FIRST RULE, and asking it here is the only moment it
                can be asked. Seeding is the one write that does not itself go through approval --
                every later revision does -- so the approvers named now are the ones who will vet
                everything that follows, including any attempt to remove them.

                At least one is required. A matrix that names nobody for itself can never be revised:
                the draft would need an approval request, the request resolves against the released
                matrix, and the released matrix asks for nothing, which the server refuses as nothing
                to submit. A realm that wants no governance at all should simply not set one up --
                that state works and is the default. */}
            {seeding ? (
                <ApprovalCellEditor open={true} heading="Who may change this matrix?"
                                    description={"Name at least one approver. Seeding is the one"
                                        + " change to this matrix that does not itself need"
                                        + " signatures; everything after it will need these."}
                                    cell={{Slots: []}} personas={personas} requireSlots={true}
                                    selfGoverning={true} saveLabel="Set up the matrix"
                                    onSave={create} onClose={() => setSeeding(false)}/>
            ) : null}

            {editing ? (
                <ApprovalCellEditor open={true} heading={editing.Heading}
                                    description={editing.Description}
                                    selfGoverning={editing.TargetType === MATRIX_TARGET}
                                    banded={!!editing.Banded} amountUpTo={editing.AmountUpTo ?? null}
                                    cell={editing} personas={personas} readOnly={!isDraft}
                                    onSave={applyCell} onClose={() => setEditing(null)}/>
            ) : null}
        </div>
    );
}
